import { useEffect, useRef, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import type { Driver } from 'driver.js'
import { RootState } from 'store/modules/rootReducer'
import { markDashboardTourSeen, dismissDashboardTour } from 'store/modules/tour'
import {
  getDashboardTourConfig,
  createDriverInstance,
  DashboardTourOptions,
} from '../utils/tourConfig'
import { trackDashboardTourShown, trackDashboardTourSkipped } from '../utils/tourTracking'
import { useTracking } from 'react-tracking'

interface UseDashboardTourProps {
  isDarkMode: boolean
  mode: 'view' | 'create' | 'edit'
  hasPlans: boolean
}

export const useDashboardTour = ({ isDarkMode, mode, hasPlans }: UseDashboardTourProps) => {
  const dispatch = useDispatch()
  const { trackEvent } = useTracking()
  const driverRef = useRef<Driver | null>(null)
  const tourState = useSelector((state: RootState) => state.tour.dashboard)

  // Check if element exists
  const elementExists = useCallback((selector: string): boolean => {
    try {
      const element = document.querySelector(selector)
      return element !== null && element instanceof HTMLElement
    } catch {
      return false
    }
  }, [])

  // Detect dashboard state from DOM
  const detectDashboardState = useCallback((): DashboardTourOptions => {
    const hasCurrentSession = elementExists('[data-tour="dashboard-current-session"]')

    return {
      hasCurrentSession,
    }
  }, [elementExists])

  // Filter steps that have existing elements
  const filterValidSteps = useCallback(
    (steps: ReturnType<typeof getDashboardTourConfig>) => {
      return steps.filter((step) => elementExists(step.element))
    },
    [elementExists]
  )

  // Start dashboard tour
  const startTour = useCallback(
    (force = false) => {
      // Don't show if already seen or dismissed (unless forced)
      if (!force && (tourState.seen || tourState.dismissed)) return

      // Don't show if not in view mode or no plans
      if (mode !== 'view' || !hasPlans) return

      // Detect dashboard state
      const dashboardOptions = detectDashboardState()

      // Get tour config with options
      const config = getDashboardTourConfig(isDarkMode, dashboardOptions)
      const validSteps = filterValidSteps(config)

      if (validSteps.length === 0) {
        // No valid elements, mark as seen and skip
        dispatch(markDashboardTourSeen())
        return
      }

      // Track tour shown
      trackEvent(trackDashboardTourShown())

      // Create driver instance with onDestroyed callback
      const handleTourComplete = () => {
        // Mark as seen when tour completes
        dispatch(markDashboardTourSeen())
      }

      const driverInstance = createDriverInstance(isDarkMode, handleTourComplete)

      // Configure driver with steps
      driverInstance.setSteps(
        validSteps.map((stepConfig) => ({
          element: stepConfig.element,
          popover: {
            title: stepConfig.popover.title,
            description: stepConfig.popover.description,
            side: stepConfig.popover.side || 'bottom',
            align: stepConfig.popover.align || 'center',
          },
        }))
      )

      // Store the instance
      driverRef.current = driverInstance

      // Start tour with a small delay to ensure DOM is ready
      setTimeout(() => {
        if (driverRef.current) {
          try {
            driverRef.current.drive()
          } catch (error) {
            console.error('Error starting dashboard tour:', error)
            // Mark as seen on error to prevent retry loops
            dispatch(markDashboardTourSeen())
          }
        }
      }, 500)
    },
    [
      isDarkMode,
      mode,
      hasPlans,
      tourState,
      dispatch,
      trackEvent,
      filterValidSteps,
      detectDashboardState,
    ]
  )

  // Skip tour
  const skipTour = useCallback(() => {
    if (driverRef.current) {
      driverRef.current.destroy()
    }
    dispatch(dismissDashboardTour())
    trackEvent(trackDashboardTourSkipped())
  }, [dispatch, trackEvent])

  // Auto-start tour when conditions are met
  useEffect(() => {
    if (mode === 'view' && hasPlans && !tourState.seen && !tourState.dismissed) {
      const timer = setTimeout(() => {
        startTour()
      }, 1000) // Longer delay for dashboard to ensure everything is loaded

      return () => clearTimeout(timer)
    }
  }, [mode, hasPlans, tourState, startTour])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (driverRef.current) {
        driverRef.current.destroy()
      }
    }
  }, [])

  return {
    startTour,
    skipTour,
  }
}
